// Map a free-form category string to one of the eight palette tokens.
// Stable across renders: the same name always returns the same colour.
// First, a handful of well-known categories get a hand-picked mapping so
// the most common groupings look intentional; everything else falls back
// to a deterministic hash. Users can override per-category via the
// Appearance settings; overrides are persisted to localStorage and read
// reactively, so re-colouring a category propagates everywhere it's
// rendered.

import { ref, watch } from 'vue'

const FIXED: Record<string, number> = {
  rent: 4,
  housing: 4,
  'rent & housing': 4,
  utilities: 2,
  groceries: 3,
  restaurants: 1,
  'food & drink': 1,
  dining: 1,
  coffee: 8,
  gas: 2,
  transport: 2,
  transportation: 2,
  'gas & auto': 2,
  shopping: 6,
  subscriptions: 5,
  entertainment: 5,
  gym: 7,
  'gym & fitness': 7,
  health: 7,
  fitness: 7,
  insurance: 4,
  income: 3,
  'credit card payment': 5,
  miscellaneous: 2,
  travel: 6,
}

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const STORAGE_KEY = 'categoryColorOverrides'

function loadOverrides(): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      const out: Record<string, number> = {}
      for (const [k, v] of Object.entries(parsed)) {
        const n = Number(v)
        if (Number.isInteger(n) && n >= 1 && n <= 8) out[k] = n
      }
      return out
    }
  } catch { /* ignore */ }
  return {}
}

export const categoryColorOverrides = ref<Record<string, number>>(loadOverrides())

watch(
  categoryColorOverrides,
  (v) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)) } catch { /* ignore */ }
  },
  { deep: true },
)

export const CATEGORY_PALETTE: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8]

function normalizeKey(name: string): string {
  return name.trim().toLowerCase()
}

// Resolves the palette slot (1-8) for a category, honouring user overrides
// first, then the hand-picked FIXED map, then a hash fallback.
export function categoryColorSlot(name: string | null | undefined): number {
  if (!name) return 0
  const key = normalizeKey(name)
  const override = categoryColorOverrides.value[key]
  if (override) return override
  const fixed = FIXED[key]
  if (fixed) return fixed
  return (hash(key) % 8) + 1
}

export function categoryColor(name: string | null | undefined): string {
  if (!name) return 'var(--ink-4)'
  return `var(--cat-${categoryColorSlot(name)})`
}

/**
 * Colours for categories drawn side by side in ONE chart, where two sharing a
 * swatch makes the chart unreadable (Rent and Insurance are both fixed to
 * slot 4). Pass names in priority order, largest first: each keeps its usual
 * colour unless an earlier one already took it, then it takes the next free
 * slot. Past eight categories repeats are unavoidable and allowed.
 */
export function distinctCategoryColors(names: string[]): Map<string, string> {
  const out = new Map<string, string>()
  const used = new Set<number>()
  for (const name of names) {
    let slot = categoryColorSlot(name) || 1
    if (used.has(slot) && used.size < 8) {
      for (let k = 1; k <= 8 && used.has(slot); k++) slot = (slot % 8) + 1
    }
    used.add(slot)
    out.set(name, `var(--cat-${slot})`)
  }
  return out
}

export function categoryDisplayName(name: string | null | undefined): string {
  if (!name) return 'Uncategorised'
  return name
}

// Set the slot for a category, or pass null to revert to the automatic default.
export function setCategoryColor(name: string, slot: number | null): void {
  if (!name) return
  const key = normalizeKey(name)
  const next = { ...categoryColorOverrides.value }
  if (slot == null) delete next[key]
  else if (Number.isInteger(slot) && slot >= 1 && slot <= 8) next[key] = slot
  else return
  categoryColorOverrides.value = next
}

export function isCategoryOverridden(name: string | null | undefined): boolean {
  if (!name) return false
  return categoryColorOverrides.value[normalizeKey(name)] != null
}

export function resetAllCategoryColors(): void {
  categoryColorOverrides.value = {}
}
